import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../helpers/AuthContext';
import './SellerAudiencePreview.css';

/**
 * Anonymous follower and per-product save counts for the acting seller's own Channel
 * (PLAN-SHOPPER-SHOP-DISCOVERY Phase 4a, `#r-seller-audience-preview` on bktestpage only). The API
 * returns numbers and product UUIDs, never people. Product names come from the seller's own product
 * list, which this preview reads once; a UUID it cannot match falls back to a short id.
 */
export default function SellerAudiencePreview() {
  const { t } = useTranslation('marketplace', { keyPrefix: 'shopDiscovery.seller' });
  const { fetch: authFetch, API_URL } = useAuth();
  const [audience, setAudience] = useState({ status: 'loading', followerCount: null });
  const [rows, setRows] = useState({ items: [], next: null, status: 'loading' });
  const names = useRef(new Map());
  const generation = useRef(0);

  const getJson = useCallback(async path => {
    const response = await authFetch(`${API_URL}/v1/seller/discovery${path}`);
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.code || String(response.status));
    return body;
  }, [authFetch, API_URL]);

  const loadAudience = useCallback(async () => {
    setAudience({ status: 'loading', followerCount: null });
    try {
      const body = await getJson('/audience');
      setAudience({ status: 'ready', followerCount: body.data.followerCount });
    } catch (error) {
      setAudience({ status: 'error', followerCount: null });
    }
  }, [getJson]);

  const loadProducts = useCallback(async afterId => {
    const mine = ++generation.current;
    setRows(current => ({ ...current, status: 'loading' }));
    try {
      if (!names.current.size) {
        const response = await authFetch(`${API_URL}/v1/seller/products`);
        const body = await response.json().catch(() => ({}));
        (body.data || []).forEach(product => names.current.set(product.uuid, product.name));
      }
      const body = await getJson(`/products/audience?limit=25${afterId ? `&afterId=${afterId}` : ''}`);
      if (mine !== generation.current) return;
      setRows(current => ({ items: [...(afterId ? current.items : []), ...body.data], status: 'ready',
        next: body.meta.hasMore ? body.meta.nextAfterId : null }));
    } catch (error) {
      if (mine === generation.current) setRows(current => ({ ...current, status: 'error' }));
    }
  }, [authFetch, API_URL, getJson]);

  useEffect(() => { loadAudience(); loadProducts(null); return () => { generation.current += 1; }; }, [loadAudience, loadProducts]);

  return (
    <div className="seller-audience-preview">
      <h3>{t('title')}</h3>
      <p>{t('description')}</p>
      <div role="status" aria-live="polite" className="seller-audience-preview__followers">
        <span className="seller-audience-preview__label">{t('followers')}</span>
        {audience.status === 'loading' ? <span>{t('loading')}</span> : null}
        {audience.status === 'ready' ? (
          audience.followerCount > 0
            ? <strong className="seller-audience-preview__count">{audience.followerCount}</strong>
            : <span>{t('followersEmpty')}</span>
        ) : null}
        {audience.status === 'error' ? <button type="button" onClick={loadAudience}>{t('retry')}</button> : null}
      </div>
      <h4>{t('products')}</h4>
      <p className="seller-audience-preview__help">{t('productsHelp')}</p>
      {rows.status === 'error' ? <p role="alert">{t('failed')} <button type="button" onClick={() => loadProducts(null)}>{t('retry')}</button></p> : null}
      <table className="seller-audience-preview__table">
        <thead><tr><th scope="col">{t('productLabel')}</th><th scope="col">{t('savedBy')}</th></tr></thead>
        <tbody>
          {rows.items.map(row => (
            <tr key={row.productUuid}>
              <td data-label={t('productLabel')}>{names.current.get(row.productUuid) || t('unknownProduct', { id: row.productUuid.slice(0, 8) })}</td>
              <td data-label={t('savedBy')}>{row.savedByCount > 0 ? row.savedByCount : t('savedEmpty')}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.status === 'loading' ? <p>{t('loading')}</p> : null}
      {rows.next ? <button type="button" onClick={() => loadProducts(rows.next)}>{t('loadMore')}</button> : null}
    </div>
  );
}
